import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import Autocomplete from '@mui/material/Autocomplete';
import LinearProgress from '@mui/material/LinearProgress';
import Layout from '../components/Layout';
import { bands } from '../theme';
import api from '../services/api';
import { useI18n } from '../i18n';
import { useMe, useCan } from '../hooks/useMe';

// La rejilla abre en horario de oficina y sólo crece si hay eventos fuera:
// una junta a las 7:00 no debe quedar recortada contra el borde de arriba.
const BASE_START = 8;
const BASE_END = 18;
const HOUR_PX = 52;

const ATTENDANCE = ['attended', 'partial', 'no_show', 'rescheduled'];
// Todos los tipos que admite la bitácora. Una junta de calendario casi siempre
// es un seguimiento, así que es el tipo con el que abre.
const KINDS = ['check_in', 'training', 'support', 'onboarding', 'qbr'];

function startOfWeek(d) {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7; // Monday = 0
  date.setDate(date.getDate() - day);
  date.setHours(0, 0, 0, 0);
  return date;
}

const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const keyOf = (a) => (a ? `${a.source_db}:${a.tenant_id}` : null);

// Una junta con dos personas del equipo vuelve con el MISMO google_event_id en
// los dos calendarios. En la vista de equipo eso son dos bloques distintos —uno
// por agenda— así que la identidad de una fila es el par dueño + evento.
const rowId = (e) => `${e.owner_email}:${e.google_event_id}`;

// Un evento pide respuesta cuando ya pasó, está vinculado y nadie dijo si
// hubo junta. Es el mismo criterio que alimenta el contador del tablero.
const needsAnswer = (e) =>
  Boolean(e.session_id) && !e.attendance && new Date(e.end || e.start) < new Date();

// Event card carried over from the work-order calendar in mpindustries-web:
// left accent in the account's health colour, rounded on the right only.
function EventCard({ event, dayStart, showOwner, onOpen }) {
  const { t, fmtTime } = useI18n();
  const start = new Date(event.start);
  const minutes = (start.getHours() - dayStart) * 60 + start.getMinutes();
  const top = (minutes / 60) * HOUR_PX;
  const height = Math.max(((event.duration_min || 60) / 60) * HOUR_PX, 26);
  const linked = Boolean(event.session_id);
  const matched = Boolean(event.tenant_id);
  const color = matched ? (bands[event.band] || bands.watch).color : '#847782';

  return (
    <Box
      onClick={() => onOpen(event)}
      sx={{
        position: 'absolute', left: 4, right: 4, top, height,
        backgroundColor: '#F5F5F5',
        borderLeft: `5px solid ${color}`,
        borderRadius: '0 10px 10px 0',
        boxShadow: '0 0 5px rgba(23,3,18,.22)',
        p: '5px 7px', overflow: 'hidden', cursor: 'pointer',
        fontSize: 11.5, fontWeight: 600, color: matched ? '#170312' : '#847782',
        lineHeight: 1.3,
        // El rayado distingue de un vistazo lo que aún no está vinculado a una
        // cuenta: la sugerencia por dominio todavía no cuenta como bitácora.
        backgroundImage: linked ? 'none'
          : 'repeating-linear-gradient(45deg, rgba(132,119,130,.13) 0 5px, transparent 5px 10px)',
      }}
    >
      <Box sx={{ fontWeight: 400, fontSize: 10.5, color: '#847782', fontVariantNumeric: 'tabular-nums' }}>
        {fmtTime(event.start)}
      </Box>
      {event.account_name || event.title || t('calendar.untitled')}
      <Box sx={{ fontWeight: 350, fontSize: 10.5, color: '#847782' }}>
        {event.account_name ? event.title : t('calendar.noAccount')}
      </Box>
      {/* En la vista de equipo la misma rejilla lleva varias agendas: sin el
          nombre no se sabe de quién es cada bloque. */}
      {showOwner && (
        <Box sx={{ fontWeight: 500, fontSize: 10, color: '#531253' }}>
          {event.owner_name}
        </Box>
      )}
      {needsAnswer(event) && (
        <Box sx={{
          display: 'inline-block', mt: 0.4, fontSize: 10, fontWeight: 600,
          backgroundColor: '#FF6666', color: '#fff', px: 0.8, borderRadius: '8px',
        }}>{t('calendar.confirmAttendance')}</Box>
      )}
    </Box>
  );
}

function Field({ label, children }) {
  if (!children) return null;
  return (
    <Box sx={{ mb: 1.2 }}>
      <Typography sx={{
        fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#847782',
      }}>{label}</Typography>
      <Typography sx={{ fontSize: 13.5 }}>{children}</Typography>
    </Box>
  );
}

// Detalle del evento: es donde se vincula con una cuenta y donde se responde
// la asistencia. Fuera de aquí el calendario es sólo lectura.
function EventDialog({ event, canLog, onClose, onChanged }) {
  const navigate = useNavigate();
  const { t, fmtDateTime, fmtTime, tApiError } = useI18n();
  const linked = Boolean(event.session_id);
  // Cuando el backend ya adivinó la cuenta por el dominio, el selector abre
  // con esa sugerencia puesta: vincular es un clic, no una búsqueda.
  const suggested = !linked && event.tenant_id
    ? { tenant_id: event.tenant_id, source_db: event.source_db, organization_name: event.account_name }
    : null;

  const [accounts, setAccounts] = useState(null);
  const [choice, setChoice] = useState(suggested);
  // Lo guardado en la sesión, para saber si hay algo que guardar.
  const [stored, setStored] = useState({ kind: 'check_in', notes: '' });
  const [kind, setKind] = useState('check_in');
  const [notes, setNotes] = useState('');
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const dirty = kind !== stored.kind || notes.trim() !== stored.notes;

  // El evento de Google no trae el tipo ni la nota: se leen de la sesión
  // vinculada. Se busca por id alrededor de la fecha del evento, que es la
  // fecha con la que se guardó al vincularla.
  useEffect(() => {
    if (!event.session_id) return;
    const at = new Date(event.start);
    api.get('/api/sessions', {
      params: {
        from: addDays(at, -7).toISOString(),
        to: addDays(at, 7).toISOString(),
      },
    })
      .then((res) => {
        const session = (res.data || []).find((x) => x.id === event.session_id);
        if (!session) return;
        const values = { kind: session.kind || 'check_in', notes: session.notes || '' };
        setStored(values);
        setKind(values.kind);
        setNotes(values.notes);
      })
      .catch(() => {});
  }, [event.session_id, event.start]);

  async function writeSession(sessionId) {
    // La nota vacía se manda como '' para que el backend la borre; null la
    // dejaría como estaba.
    await api.patch(`/api/sessions/${sessionId}`, { kind, notes: notes.trim() });
    setStored({ kind, notes: notes.trim() });
  }

  // El catálogo sólo se pide cuando de verdad hay que elegir a mano.
  useEffect(() => {
    if (linked || accounts) return;
    api.get('/api/accounts', { params: { scope: 'managed' } })
      .then((res) => setAccounts(res.data.accounts || []))
      .catch(() => setAccounts([]));
  }, [linked, accounts]);

  async function link() {
    if (!choice) return;
    setBusy(true);
    setError(null);
    try {
      const { data: session } = await api.post('/api/calendar/link', {
        google_event_id: event.google_event_id,
        tenant_id: choice.tenant_id,
        source_db: choice.source_db,
        title: event.title,
        scheduled_at: event.start,
        duration_min: event.duration_min,
      });
      // El vínculo crea la sesión como seguimiento y sin nota; lo capturado
      // aquí se escribe enseguida sobre ella.
      if (dirty) await writeSession(session.id);
      await onChanged(rowId(event));
    } catch (err) {
      setError(tApiError(err, 'calendar.linkError'));
    } finally {
      setBusy(false);
    }
  }

  async function saveSession() {
    setBusy(true);
    setError(null);
    try {
      await writeSession(event.session_id);
      await onChanged(rowId(event));
      setSaved(true);
    } catch (err) {
      setError(tApiError(err, 'calendar.sessionSaveError'));
    } finally {
      setBusy(false);
    }
  }

  async function answer(attendance) {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/api/sessions/${event.session_id}/attendance`, { attendance });
      await onChanged(rowId(event));
    } catch (err) {
      setError(tApiError(err, 'calendar.attendanceError'));
    } finally {
      setBusy(false);
    }
  }

  const when = event.all_day
    ? t('calendar.allDay')
    : `${fmtDateTime(event.start)} – ${fmtTime(event.end)}`;

  return (
    <Dialog
      open onClose={onClose} maxWidth="sm" fullWidth
      slotProps={{ paper: { sx: { border: '1px solid #E4E0E2' } } }}
    >
      <DialogTitle sx={{
        fontSize: 17, fontWeight: 600, borderBottom: '1px solid #E4E0E2',
        display: 'flex', alignItems: 'flex-start', gap: 1.5,
      }}>
        <Box sx={{ flexGrow: 1 }}>{event.title || t('calendar.untitled')}</Box>
        <Box
          component="button" onClick={onClose} aria-label={t('common.close')}
          sx={{
            border: 'none', background: 'none', cursor: 'pointer',
            fontSize: 20, color: '#847782', lineHeight: 1, fontFamily: 'inherit',
          }}
        >×</Box>
      </DialogTitle>

      {busy && <LinearProgress />}

      <DialogContent sx={{ pt: 2.5 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Field label={t('calendar.when')}>{when}</Field>
        <Field label={t('calendar.location')}>{event.location}</Field>
        <Field label={t('calendar.organizer')}>{event.organizer}</Field>
        <Field label={t('calendar.attendees')}>
          {event.attendees?.length ? event.attendees.join(', ') : null}
        </Field>

        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #E4E0E2' }}>
          <Typography sx={{
            fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase',
            color: '#847782', mb: 1,
          }}>{t('calendar.account')}</Typography>

          {linked ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                {event.account_name || t('common.dash')}
              </Typography>
              <Button
                size="small" variant="outlined"
                onClick={() => navigate(`/accounts/${event.tenant_id}?source=${event.source_db}`)}
              >{t('calendar.openAccount')}</Button>
            </Box>
          ) : (
            <>
              <Typography sx={{ fontSize: 12.5, color: '#847782', mb: 1.2 }}>
                {t(`calendar.reason.${event.match_reason || 'no_match'}`)}
              </Typography>

              {canLog ? (
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Autocomplete
                    sx={{ flex: 1, minWidth: 240 }}
                    options={event.candidates?.length ? event.candidates : (accounts || [])}
                    loading={!accounts}
                    value={choice}
                    onChange={(_, value) => setChoice(value)}
                    getOptionLabel={(o) => o.organization_name || o.tenant_id}
                    isOptionEqualToValue={(o, v) => keyOf(o) === keyOf(v)}
                    renderInput={(params) => (
                      <TextField {...params} size="small" placeholder={t('calendar.pickAccount')} />
                    )}
                  />
                  <Button variant="contained" disabled={!choice || busy} onClick={link}>
                    {t('calendar.link')}
                  </Button>
                </Box>
              ) : (
                <Typography sx={{ fontSize: 13, color: '#847782' }}>
                  {t('calendar.linkNoPermission')}
                </Typography>
              )}
            </>
          )}
        </Box>

        {/* Qué fue la reunión y qué se habló. Sin vincular todavía se captura
            igual y viaja con el vínculo; ya vinculada se guarda aparte. */}
        {canLog && (
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #E4E0E2' }}>
            <Typography sx={{
              fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase',
              color: '#847782', mb: 1,
            }}>{t('calendar.session')}</Typography>
            <TextField
              select size="small" fullWidth
              label={t('sessionsLog.kind')}
              value={kind}
              onChange={(e) => { setKind(e.target.value); setSaved(false); }}
              sx={{ mb: 1.5 }}
            >
              {KINDS.map((k) => (
                <MenuItem key={k} value={k} sx={{ fontSize: 13.5 }}>{t(`kinds.${k}`)}</MenuItem>
              ))}
            </TextField>
            <TextField
              multiline minRows={3} size="small" fullWidth
              placeholder={t('sessionsLog.notesPlaceholder')}
              value={notes}
              onChange={(e) => { setNotes(e.target.value); setSaved(false); }}
            />
            {linked ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.2 }}>
                <Button
                  size="small" variant="contained"
                  disabled={!dirty || busy} onClick={saveSession}
                >{t('calendar.saveSession')}</Button>
                {saved && !dirty && (
                  <Typography sx={{ fontSize: 12, color: '#847782' }}>{t('calendar.sessionSaved')}</Typography>
                )}
              </Box>
            ) : (
              <Typography sx={{ fontSize: 12, color: '#847782', mt: 0.8 }}>
                {t('calendar.sessionHint')}
              </Typography>
            )}
          </Box>
        )}

        {linked && canLog && (
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #E4E0E2' }}>
            <Typography sx={{
              fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase',
              color: '#847782', mb: 1,
            }}>{t('calendar.attendanceQuestion')}</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {ATTENDANCE.map((value) => (
                <Button
                  key={value} size="small" disabled={busy}
                  variant={event.attendance === value ? 'contained' : 'outlined'}
                  onClick={() => answer(value)}
                >{t(`attendance.${value}`)}</Button>
              ))}
            </Box>
          </Box>
        )}

        {event.html_link && (
          <Box sx={{ mt: 2.5 }}>
            <Typography
              component="a" href={event.html_link} target="_blank" rel="noreferrer"
              sx={{ fontSize: 12.5, color: '#FF6666' }}
            >{t('calendar.openInGoogle')}</Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Calendar() {
  const { me } = useMe();
  const canLog = useCan('log_activity');
  const { t, fmtMonthYear, fmtWeekdayShort, tApiError } = useI18n();

  const [anchor, setAnchor] = useState(() => startOfWeek(new Date()));
  const [directory, setDirectory] = useState(null);
  const [owner, setOwner] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [failed, setFailed] = useState([]);

  const days = useMemo(() => Array.from({ length: 5 }, (_, i) => addDays(anchor, i)), [anchor]);
  const from = useMemo(() => anchor.toISOString(), [anchor]);
  const to = useMemo(() => addDays(anchor, 7).toISOString(), [anchor]);

  // Qué calendarios puede abrir esta persona lo decide el backend; aquí sólo
  // se dibuja el selector y se elige el propio por omisión.
  useEffect(() => {
    api.get('/api/calendar/calendars')
      .then((res) => {
        setDirectory(res.data);
        const mine = res.data.calendars.find(
          (c) => (c.email || '').toLowerCase() === (me?.email || '').toLowerCase()
        );
        setOwner(mine?.email || res.data.calendars[0]?.email || me?.email || null);
      })
      .catch((err) => {
        setDirectory({ calendars: [], configured: true });
        setError(tApiError(err, 'calendar.calendarsError'));
        setLoading(false);
      });
  }, [me, tApiError]);

  const load = useCallback(async () => {
    if (!owner || !directory || directory.configured === false) {
      if (directory) setLoading(false);
      return [];
    }
    setLoading(true);
    try {
      const res = await api.get('/api/calendar/events', { params: { owner, from, to } });
      const list = res.data.events || [];
      setEvents(list);
      setFailed(res.data.failed || []);
      setError(null);
      return list;
    } catch (err) {
      setEvents([]);
      setFailed([]);
      setError(tApiError(err, 'calendar.loadError'));
      return [];
    } finally {
      setLoading(false);
    }
  }, [owner, from, to, directory, tApiError]);

  useEffect(() => { load(); }, [load]);

  // Tras vincular o responder asistencia el evento se relee para que el
  // diálogo muestre el estado nuevo sin cerrarse.
  const refresh = useCallback(async (eventId) => {
    const list = await load();
    setSelectedId(list.some((e) => rowId(e) === eventId) ? eventId : null);
  }, [load]);

  const { timed, allDay, dayStart, dayEnd } = useMemo(() => {
    const t1 = events.filter((e) => !e.all_day && e.start);
    const t2 = events.filter((e) => e.all_day);
    let start = BASE_START;
    let end = BASE_END;
    for (const e of t1) {
      const s = new Date(e.start);
      const f = new Date(e.end || e.start);
      start = Math.min(start, s.getHours());
      end = Math.max(end, f.getHours() + (f.getMinutes() ? 1 : 0));
    }
    return { timed: t1, allDay: t2, dayStart: start, dayEnd: Math.max(end, start + 1) };
  }, [events]);

  const hours = Array.from({ length: dayEnd - dayStart }, (_, i) => dayStart + i);
  const today = new Date();
  const selected = events.find((e) => rowId(e) === selectedId) || null;
  const notConfigured = directory && directory.configured === false;

  return (
    <Layout>
      {notConfigured && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('calendar.notConfigured', { missing: (directory.missing || []).join(', ') })}
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {/* Un calendario ilegible en la vista de equipo se nombra: callarlo haría
          creer que esa persona tiene la semana libre. */}
      {failed.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('calendar.someFailed', { owners: failed.map((f) => f.owner).join(', ') })}
        </Alert>
      )}

      <Paper sx={{ overflow: 'hidden' }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap',
          px: 2, py: 1.4, borderBottom: '1px solid #E4E0E2',
        }}>
          <IconButton
            size="small" aria-label={t('calendar.prevWeek')}
            onClick={() => setAnchor(addDays(anchor, -7))} sx={{ color: '#FF6666' }}
          >‹</IconButton>
          <IconButton
            size="small" aria-label={t('calendar.nextWeek')}
            onClick={() => setAnchor(addDays(anchor, 7))} sx={{ color: '#FF6666' }}
          >›</IconButton>
          <Typography sx={{ fontSize: 17, fontWeight: 600, textTransform: 'capitalize' }}>
            {fmtMonthYear(anchor)}
          </Typography>

          {/* Sólo para quien puede ver más que el suyo. «Todos» sobrepone la
              semana del equipo entero en la misma rejilla. */}
          {directory?.can_view_all && (
            <TextField
              select size="small" variant="standard"
              value={owner || ''}
              onChange={(e) => setOwner(e.target.value)}
              label={t('calendar.ownerLabel')}
              sx={{ minWidth: 200, ml: 1, '& .MuiSelect-select': { fontSize: 13.5, fontFamily: 'Saira' } }}
            >
              <MenuItem value="all" sx={{ fontSize: 13.5, fontWeight: 600 }}>
                {t('calendar.allCalendars', { count: directory.calendars.length })}
              </MenuItem>
              {directory.calendars.map((c) => (
                <MenuItem key={c.email} value={c.email} sx={{ fontSize: 13.5 }}>
                  {c.display_name || c.email}
                  {!c.access_granted && (
                    <Box component="span" sx={{ color: '#847782', fontSize: 11, ml: 1 }}>
                      · {t('calendar.noConsoleAccess')}
                    </Box>
                  )}
                </MenuItem>
              ))}
            </TextField>
          )}

          <Typography sx={{ ml: 'auto', fontSize: 12, color: '#847782' }}>
            {t('calendar.footnote')}
          </Typography>
        </Box>

        {loading && <LinearProgress />}

        {allDay.length > 0 && (
          <Box sx={{
            display: 'grid', gridTemplateColumns: '52px repeat(5, 1fr)',
            borderBottom: '1px solid #E4E0E2',
          }}>
            <Box sx={{
              fontSize: 10, color: '#847782', textAlign: 'right', pr: 0.9, pt: 0.8,
            }}>{t('calendar.allDay')}</Box>
            {days.map((d) => (
              <Box key={d.toISOString()} sx={{ borderLeft: '1px solid #F0ECEE', p: 0.5 }}>
                {allDay
                  .filter((e) => sameDay(new Date(e.start), d))
                  .map((e) => (
                    <Box
                      key={rowId(e)}
                      onClick={() => setSelectedId(rowId(e))}
                      sx={{
                        fontSize: 11, fontWeight: 600, cursor: 'pointer', mb: 0.4,
                        backgroundColor: '#F0ECEE', borderRadius: '4px', px: 0.8, py: 0.3,
                        borderLeft: `4px solid ${e.tenant_id ? (bands[e.band] || bands.watch).color : '#847782'}`,
                      }}
                    >{e.account_name || e.title || t('calendar.untitled')}</Box>
                  ))}
              </Box>
            ))}
          </Box>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: `52px repeat(5, 1fr)`, overflowX: 'auto' }}>
          <Box sx={{ borderBottom: '1px solid #E4E0E2' }} />
          {days.map((d) => (
            <Box key={d.toISOString()} sx={{
              textAlign: 'center', py: 1.1, borderBottom: '1px solid #E4E0E2',
              borderLeft: '1px solid #F0ECEE',
            }}>
              <Box sx={{ fontSize: 10.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#847782' }}>
                {fmtWeekdayShort(d)}
              </Box>
              <Box sx={{
                fontSize: 17, fontWeight: sameDay(d, today) ? 600 : 500,
                fontVariantNumeric: 'tabular-nums',
                ...(sameDay(d, today) && {
                  backgroundColor: '#FF6666', color: '#fff', borderRadius: '50%',
                  width: 29, height: 29, display: 'inline-grid', placeItems: 'center',
                }),
              }}>{d.getDate()}</Box>
            </Box>
          ))}

          <Box sx={{ borderRight: '1px solid #F0ECEE' }}>
            {hours.map((h) => (
              <Box key={h} sx={{
                height: HOUR_PX, fontSize: 10.5, color: '#847782', textAlign: 'right',
                pr: 0.9, transform: 'translateY(-6px)', fontVariantNumeric: 'tabular-nums',
              }}>{String(h).padStart(2, '0')}:00</Box>
            ))}
          </Box>

          {days.map((d) => (
            <Box key={d.toISOString()} sx={{
              position: 'relative', borderLeft: '1px solid #F0ECEE',
              minHeight: hours.length * HOUR_PX,
              backgroundImage: `repeating-linear-gradient(to bottom, #F0ECEE 0 1px, transparent 1px ${HOUR_PX}px)`,
            }}>
              {timed
                .filter((e) => sameDay(new Date(e.start), d))
                .map((e) => (
                  <EventCard
                    key={rowId(e)}
                    event={e}
                    dayStart={dayStart}
                    showOwner={owner === 'all'}
                    onOpen={(x) => setSelectedId(rowId(x))}
                  />
                ))}
            </Box>
          ))}
        </Box>

        {!loading && !error && events.length === 0 && !notConfigured && (
          <Typography sx={{ p: 2, fontSize: 13, color: '#847782', textAlign: 'center' }}>
            {t('calendar.noEvents')}
          </Typography>
        )}
      </Paper>

      {selected && (
        <EventDialog
          key={rowId(selected)}
          event={selected}
          canLog={canLog}
          onClose={() => setSelectedId(null)}
          onChanged={refresh}
        />
      )}
    </Layout>
  );
}
